// src/services/api.ts
const API = '/api/index.php';
export const PHOTO_BASE = 'https://roopcottage.co.uk/';

async function call(action: string, body: Record<string, unknown> = {}, method: 'GET' | 'POST' = 'POST') {
  const params = new URLSearchParams();
  params.set('action', action);
  if (method === 'GET') {
    Object.entries(body).forEach(([k, v]) => { if (v !== undefined && v !== '') params.set(k, String(v)); });
  }
  const res = await fetch(`${API}?${params.toString()}`, {
    method,
    credentials: 'include',
    headers: method === 'GET' ? undefined : { 'Content-Type': 'application/json' },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  });
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) throw new Error('API not reachable or session expired - log in again.');
  const data = await res.json();
  if (!res.ok || data.error) throw new Error(data.error || 'Request failed');
  return data;
}

async function upload(action: string, form: FormData) {
  const res = await fetch(`${API}?action=${action}`, { method: 'POST', credentials: 'include', body: form });
  const data = await res.json();
  if (!res.ok || data.error) throw new Error(data.error || 'Upload failed');
  return data;
}
export const auditApi = {
  templates:      () => call('audit_templates', {}, 'GET'),
  syncTemplates:  (templates: unknown) => call('audit_sync_templates', { templates }),
  list:           (template_id?: string) => call('audit_list', template_id ? { template_id } : {}, 'GET'),
  start:          (template_id: string) => call('audit_start', { template_id }),
  submit:         (payload: Record<string, unknown>) => call('audit_submit', payload),
  actionPlans:    (status?: string) => call('action_plans', status ? { status } : {}, 'GET'),
  startAction:    (id: string) => call('action_plans', { id, status: 'in-progress' }),
  closeAction:    (id: string, closure_evidence: string) => call('action_plan_close', { id, closure_evidence }),
  uploadEvidence: (form: FormData) => upload('upload_evidence', form),
  deleteEvidence: (id: number) => call('delete_evidence', { id }),
  checkList:      () => call('inspection_checklist', {}, 'GET'),
  saveCheckList:  (item_key: string, status: string) => call('inspection_checklist', { item_key, status }),
  checklist:      () => call('inspection_checklist', {}, 'GET'),
  saveChecklist:  (item_key: string, status: string) => call('inspection_checklist', { item_key, status }),
  sendAlerts:     () => call('overdue_alerts', {}, 'POST'),
  evidencePack:   (kq: string) => call('evidence_pack', { kq }, 'GET'),
  policyLog:      (payload: Record<string, unknown>) => call('policy_log', payload),
  policyLogList:  () => call('policy_log_list', {}, 'GET'),
  policySignoff:  (ref: string, staff_user_id: number, role: string) => call('policy_signoff', { ref, staff_user_id, confirmed_by_role: role }),
  getUsers:       () => call('get_users', {}, 'GET'),
};
// src/services/api.ts additions

export interface ApiUser {
  id: number;
  role: "SuperAdmin" | "RegisteredManager" | "DeputyManager" | "Auditor" | "Staff";
  name: string;
  email: string;
}

export interface PolicyLogRequest {
  policy_id: string;
  page_number: number;
  seconds_on_page: number;
  total_pages: number;
  reading_token?: string;
}

export interface PolicySignoffRequest {
  policy_id: string;
  declaration: boolean;
  quiz_score: number;
  quiz_passed: boolean;
  answers?: Array<{ question_id: string; selected: string; correct: boolean }>;
}

export interface PolicyLogRecord {
  id: number;
  user_id: number;
  user_name?: string;
  policy_id: string;
  page_number: number;
  seconds_on_page: number;
  total_pages: number;
  created_at: string;
}

export function storedUser(): any {
  try {
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      try {
        const value = JSON.parse(localStorage.getItem(key) || "null");

        if (value && typeof value === "object" && (value.role || value.email) && (value.id || value.user_id)) {
          return value;
        }
      } catch (error) {
        continue;
      }
    }
  } catch (error) {
    return null;
  }

  return null;
}

export async function getUsers(): Promise<ApiUser[]> {
  return auditApi.getUsers() as Promise<ApiUser[]>;
}

export async function policyLog(payload: PolicyLogRequest): Promise<{ ok: boolean; reading_token?: string }> {
    return auditApi.policyLog(payload as unknown as Record<string, unknown>) as Promise<{ ok: boolean; reading_token?: string }>;
}

export async function policyLogList(params?: { policy_id?: string; user_id?: number }): Promise<PolicyLogRecord[]> {
  return auditApi.policyLogList() as Promise<PolicyLogRecord[]>;
}

export async function policySignoff(payload: PolicySignoffRequest): Promise<{ ok: boolean; signoff_id: number }> {
  const user = storedUser();

  return auditApi.policySignoff(
    payload.policy_id,
    user && user.id ? Number(user.id) : 0,
    user && user.role ? String(user.role) : "Staff"
  ) as Promise<{ ok: boolean; signoff_id: number }>;
}